// Project-specific estimator ledger for QuoteMaker Internal Pricing.
//
// QuoteMaker's stored costBreakdown mixes two valid commercial authorities:
// final customer charge-out lines from Pricing, and unmatched business-cost
// estimates that still pass through the cost model. This adapter presents both
// in one ex-GST estimating view without rewriting the global rate card.

import { calculateProjectTotals } from './cost-math';
import { buildCostConfig, mapScopeToInputs } from './cost-math-adapter';
import { partitionCostLines } from './cost-line-partition';
import { BusinessPricingService } from './pricing';
import {
    clearCurrentPricingResolutionState,
    pricingResolutionIdentity,
    pricingRowHasPendingResolution,
    pricingRowRateBookMatch,
    reconcileScopeItemPricingResolution,
    scopeItemHasUnresolvedPricing,
} from './pricing-resolution';
import {
    priceWorkingBillableQuantity,
    priceWorkingRateField,
} from '../utils/price-working-edit';
import { normalisePricingUnitKey } from '../utils/pricing-unit-key';
import {
    buildStage4PricingLedger,
    isManagedZeroCostReceipt,
    readMethodologyBridgeStamps,
} from '../utils/quote-pricing-readiness';

export const INTERNAL_PRICING_CATEGORIES = [
    'materials', 'labour', 'equipment', 'subcontractors', 'otherCosts',
];

const CATEGORY_LABELS = {
    materials: 'Materials',
    labour: 'Labour',
    equipment: 'Equipment and hire',
    subcontractors: 'Subcontractors',
    otherCosts: 'Other costs',
};

const MATCH_STAMPS = [
    'matchedName', 'matched_name', 'matchedUnit', 'matched_unit',
    'matchedRate', 'matched_rate', 'matchedBy', 'matched_by',
    'matchConfidence', 'match_confidence', 'confidence',
];

const PRICING_DRIVER_LABELS = Object.freeze({
    invalid_material_quantity: 'Quantity needed',
    invalid_labour_duration: 'Days needed',
    invalid_labour_hours: 'Hours needed',
    invalid_labour_workers: 'Crew needed',
    invalid_labour_quantity: 'Quantity needed',
    invalid_equipment_quantity: 'Quantity needed',
    invalid_equipment_duration: 'Days needed',
    invalid_subcontractor_duration: 'Days needed',
    invalid_subcontractor_workers: 'Crew needed',
    invalid_subcontractor_quantity: 'Quantity needed',
});

const SCOPE_LUMP_SUM_SOURCE = 'scope_lump_sum';

function hasSystemManagedAuthority(value) {
    if (value == null || value === false) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return Boolean(value);
}

function isSystemManaged(value) {
    return !!value && (
        hasSystemManagedAuthority(value.systemManaged)
        || hasSystemManagedAuthority(value.system_managed)
    );
}

function otherCostsSourceKey(breakdown) {
    const canonical = Array.isArray(breakdown?.otherCosts) ? breakdown.otherCosts : null;
    const legacy = Array.isArray(breakdown?.other) ? breakdown.other : null;
    if (canonical?.length > 0) return 'otherCosts';
    if (legacy?.length > 0) return 'other';
    if (canonical) return 'otherCosts';
    if (legacy) return 'other';
    return 'otherCosts';
}

const number = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const parsed = Number(String(value ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};
const round2 = (value) => Math.round((number(value) + Number.EPSILON) * 100) / 100;
const nullableRound2 = (value) => value == null || !Number.isFinite(Number(value)) ? null : round2(value);
const exGst = (value, basis, gstExempt = false) => round2(
    gstExempt || String(basis || '').toLowerCase() === 'ex_gst'
        ? number(value)
        : number(value) / 1.1,
);

function hasManualCommercialAuthority(row) {
    const pricingStatuses = [row?.pricingStatus, row?.pricing_status].map(normalizedCommercialText);
    const rateSources = [row?._rateSource, row?._rate_source].map(normalizedCommercialText);
    return row?.manualOverride === true
        || pricingStatuses.some(status => ['manual_override', 'manual_cost_estimate'].includes(status))
        || rateSources.some(source => [
            'project_manual', 'manual', 'manual_override', 'project_cost_estimate',
        ].includes(source));
}

function declaresFinalChargeOut(row) {
    return row?.rateIsFinalChargeOut === true
        || row?.rate_is_final_charge_out === true
        || row?.rateIsFinalIncGst === true
        || row?.rate_is_final_inc_gst === true;
}

function isFinalChargeOut(row) {
    return declaresFinalChargeOut(row)
        && (!pricingRowHasPendingResolution(row) || hasManualCommercialAuthority(row));
}

function keepsSavedCommercialValuesForReview(row, category) {
    if (!declaresFinalChargeOut(row) || !pricingRowHasPendingResolution(row)) return false;
    const unresolvedReason = normalizedCommercialText(
        row?.unresolvedReason ?? row?.unresolved_reason,
    );
    if (category === 'labour' && unresolvedReason === 'included_labour_overlap') return true;
    return category === 'subcontractors'
        && ['supplier_selection_required', 'invalid_pricing_item_id'].includes(unresolvedReason);
}

function lineLabel(row, category) {
    if (category === 'labour' || category === 'subcontractors') {
        return String(row?.trade || row?.description || row?.name || 'Untitled line').trim();
    }
    return String(row?.description || row?.name || row?.trade || 'Untitled line').trim();
}

function lineUnit(row) {
    return String(row?.unit || row?.matchedUnit || row?.matched_unit || '').trim();
}

function lineQuantity(row, category) {
    return round2(priceWorkingBillableQuantity(row, category));
}

function pricingDriverLabel(row, unresolvedReason) {
    if (unresolvedReason === 'invalid_subcontractor_quantity'
        && /hour|\bhr\b/.test(lineUnit(row).toLowerCase())) {
        return 'Hours needed';
    }
    return PRICING_DRIVER_LABELS[unresolvedReason] || null;
}

function allocateWeightedCents(totalAmount, rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const totalCents = Math.max(0, Math.round(number(totalAmount) * 100));
    const costWeights = rows.map(row => Math.max(0, Math.round(number(row.businessCostTotalExGst) * 100)));
    const weightTotal = costWeights.reduce((sum, weight) => sum + weight, 0);
    const weights = weightTotal > 0 ? costWeights : rows.map(() => 1);
    const denominator = weights.reduce((sum, weight) => sum + weight, 0);
    const allocations = weights.map((weight, index) => {
        const weightedTotal = totalCents * weight;
        const cents = denominator > 0 ? Math.floor(weightedTotal / denominator) : 0;
        return {
            index,
            cents,
            remainderWeight: denominator > 0 ? weightedTotal % denominator : 0,
        };
    });
    let remainder = totalCents - allocations.reduce((sum, allocation) => sum + allocation.cents, 0);
    const remainderOrder = [...allocations].sort((left, right) => (
        right.remainderWeight - left.remainderWeight || left.index - right.index
    ));
    for (let index = 0; remainder > 0; index = (index + 1) % remainderOrder.length) {
        remainderOrder[index].cents += 1;
        remainder -= 1;
    }
    return allocations.sort((left, right) => left.index - right.index).map(allocation => allocation.cents);
}

function costRateForFinal(row, gstExempt) {
    const raw = row?.costRate ?? row?.cost_rate;
    if (raw == null || raw === '') return null;
    const parsed = Number(String(raw).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    const source = normalizedCommercialText(row?.costRateSource ?? row?.cost_rate_source);
    if (parsed === 0 && !['project_manual', 'builder_confirmed_zero'].includes(source)) return null;
    return exGst(parsed, row?.costRateTaxBasis ?? row?.cost_rate_tax_basis, gstExempt);
}

function sellRateForFinal(row, category, gstExempt) {
    const field = priceWorkingRateField(row, category);
    const raw = row?.[field];
    if (raw == null || raw === '') return null;
    const basis = row?.rateTaxBasis ?? row?.rate_tax_basis;
    const legacyInc = row?.rateIsFinalIncGst === true && !basis;
    return exGst(raw, legacyInc ? 'inc_gst' : basis, gstExempt);
}

/**
 * Cost sources that mean a builder decided the cost is zero. The same two sources the final
 * charge-out branch (costRateForFinal) and the canonical Stage 4 ledger (isKnownZeroBusinessCost
 * in utils/quote-pricing-readiness.ts) accept for a zero.
 */
const CONFIRMED_ZERO_COST_SOURCES = Object.freeze(['project_manual', 'builder_confirmed_zero']);

function declaresConfirmedZeroCostSource(row) {
    return CONFIRMED_ZERO_COST_SOURCES.includes(
        normalizedCommercialText(row?.costRateSource ?? row?.cost_rate_source),
    );
}

function buildRawLine(
    row,
    category,
    rowIndex,
    scopeIndex,
    gstExempt,
    itemLocked = false,
    managedZeroCost = false,
    passThroughAtCost = false,
) {
    const final = isFinalChargeOut(row);
    const savedCommercialReview = !final && keepsSavedCommercialValuesForReview(row, category);
    const displayFinalValues = final || savedCommercialReview;
    const quantity = lineQuantity(row, category);
    const total = number(row?.total);
    const locked = itemLocked || isSystemManaged(row);
    const pricingStatuses = [row?.pricingStatus, row?.pricing_status].map(normalizedCommercialText);
    const rateSources = [row?._rateSource, row?._rate_source].map(normalizedCommercialText);
    const manualCommercialAuthority = hasManualCommercialAuthority(row);
    const pendingUnknownCost = pricingRowHasPendingResolution(row)
        && !manualCommercialAuthority
        && !savedCommercialReview;
    // A modelled line's business cost is its own stated total. A total of zero is a real cost of
    // nothing only when BOTH hold: somebody said so (the builder typed it, marked it confirmed, or the
    // item is system-managed) AND there is a quantity to price. A typed zero RATE is a decision about
    // the rate, not evidence of the quantity: with no quantity the total is zero whatever the rate
    // is, so that zero stays unknown. Otherwise the zero is a quantity or rate nobody has supplied,
    // and the cost is unknown, never a confirmed zero (6.0.93 live review). This does not depend on
    // what the customer is charged: a free or included item can still cost the builder something.
    const unconfirmedZeroModelledCost = !displayFinalValues
        && !pendingUnknownCost
        && !locked
        && round2(total) === 0
        && !((manualCommercialAuthority || declaresConfirmedZeroCostSource(row)) && quantity > 0);
    const sellBasis = row?.rateTaxBasis ?? row?.rate_tax_basis;
    const legacyInc = row?.rateIsFinalIncGst === true && !sellBasis;
    const sellTotalExGst = displayFinalValues
        ? exGst(total, legacyInc ? 'inc_gst' : sellBasis, gstExempt)
        : null;
    const finalCostRateExGst = displayFinalValues ? costRateForFinal(row, gstExempt) : null;
    // A managed statutory package's zero-cost receipt passes through at cost, as in the Stage 4
    // ledger (pricing-ledger.ts isManagedZeroCostReceipt): its final line with no cost rate of its
    // own costs what it charges, so it carries no margin. Read as a zero cost it showed the whole
    // package as profit (100%, RBB/GPSC 2026-11-04).
    const passesThrough = displayFinalValues && passThroughAtCost
        && finalCostRateExGst == null && sellTotalExGst != null;
    const businessCostRateExGst = displayFinalValues
        ? (passesThrough
            ? (quantity > 0 ? round2(sellTotalExGst / quantity) : sellTotalExGst)
            : (finalCostRateExGst == null && managedZeroCost ? 0 : finalCostRateExGst))
        : (pendingUnknownCost || unconfirmedZeroModelledCost)
        ? null
        : (quantity > 0 ? round2(total / quantity) : number(row?.[priceWorkingRateField(row, category)]));
    const businessCostTotalExGst = displayFinalValues
        ? (passesThrough
            ? sellTotalExGst
            : (businessCostRateExGst == null ? null : round2(businessCostRateExGst * quantity)))
        : (pendingUnknownCost || unconfirmedZeroModelledCost)
        ? null
        : round2(total);
    const declaredSellRateExGst = displayFinalValues ? sellRateForFinal(row, category, gstExempt) : null;
    const sellRateExGst = displayFinalValues && declaredSellRateExGst == null && quantity > 0
        ? round2(sellTotalExGst / quantity)
        : declaredSellRateExGst;
    const manualSellAuthority = row?.manualOverride === true
        || pricingStatuses.includes('manual_override')
        || rateSources.some(source => ['project_manual', 'manual', 'manual_override'].includes(source));
    const unresolvedReason = normalizedCommercialText(row?.unresolvedReason ?? row?.unresolved_reason);
    // The driver is named from the reason code when there is one. A pending MATERIAL with no quantity needs that
    // quantity whatever marked it pending (an AI estimate, no_catalogue_match, no marker but `_rateSource: ai`): a
    // reason-code-only test hid the missing quantity behind "customer sell required" on rows no sell can attach to.
    // Scoped to materials, the evidenced case: another category with no reason code keeps its existing behaviour.
    const reasonDriverLabel = locked ? null : pricingDriverLabel(row, unresolvedReason);
    const missingMaterialQuantity = !locked && reasonDriverLabel == null && category === 'materials'
        && pendingUnknownCost && !displayFinalValues && !(quantity > 0);
    const driverNeededLabel = reasonDriverLabel || (missingMaterialQuantity ? 'Quantity needed' : null);
    // A quantity does not settle the rate. A driver row stays an open rate decision unless it carries an ACTUAL
    // rate-book match (identity plus match-time metadata): then the quantity is the only thing missing.
    const rateDecisionPending = driverNeededLabel != null
        && category === 'materials'
        && pendingUnknownCost
        && pricingRowRateBookMatch(row) == null;

    return {
        id: `${scopeIndex}:${category}:${rowIndex}`,
        scopeIndex,
        category,
        categoryLabel: CATEGORY_LABELS[category],
        rowIndex,
        label: lineLabel(row, category),
        unit: lineUnit(row),
        quantity,
        quantityField: resolveQuantityField(row, category),
        businessCostRateExGst,
        sellRateExGst,
        businessCostTotalExGst,
        sellTotalExGst,
        grossProfitExGst: null,
        grossMarginPct: null,
        sellAuthority: final
            ? (manualSellAuthority
                ? 'project_override'
                : 'rate_card')
            : 'modelled',
        savedCommercialReview,
        activePricingItemId: row?.pricingItemId || row?.pricing_item_id || null,
        sourcePricingItemId: row?.sourcePricingItemId || row?.source_pricing_item_id
            || row?.pricingItemId || row?.pricing_item_id || null,
        sourceName: row?.matchedName || row?.matched_name || null,
        contractorName: category === 'subcontractors' ? (row?.contractorName || row?.matchedContractorName || null) : null,
        supplierId: category === 'subcontractors' ? (row?.supplierId || row?.matchedSupplierId || null) : null,
        costRateSource: row?.costRateSource || row?.cost_rate_source || null,
        rateSource: row?._rateSource || row?._rate_source || null,
        confirmableManualAuthority: row?.manualOverride === true
            || ['manual_override', 'manual_cost_estimate'].includes(normalizedCommercialText(
                row?.pricingStatus ?? row?.pricing_status,
            ))
            || ['manual', 'manual_override', 'project_manual', 'project_cost_estimate'].includes(
                normalizedCommercialText(row?._rateSource ?? row?._rate_source),
            ),
        pendingResolutionIdentity: pricingRowHasPendingResolution(row)
            ? `${scopeIndex}:${category}:${rowIndex}`
            : null,
        includedLabourOverlapReview: unresolvedReason === 'included_labour_overlap',
        includedLabourOverlapPackages: Array.isArray(row?.includedLabourOverlapPackages)
            ? row.includedLabourOverlapPackages.map(value => String(value || '').trim()).filter(Boolean)
            : [],
        includedLabourOverlapPurpose: String(
            row?.includedLabourOverlapPurpose || row?.included_labour_overlap_purpose || '',
        ).trim(),
        driverNeeded: driverNeededLabel != null,
        driverNeededLabel,
        rateDecisionPending,
        // Set by buildInternalPricingLedger once residual sell has been allocated: a modelled row that could not
        // receive any (no quantity and no known cost) cannot be asked for a sell.
        sellDeferred: false,
        costKnown: businessCostTotalExGst != null,
        locked,
        syntheticSource: row?.internalPricingSource || row?.internal_pricing_source || null,
    };
}

function managedBreakdownDeclaresZeroCost(item, breakdown) {
    if (!isSystemManaged(item) || !breakdown || typeof breakdown !== 'object') return false;
    const values = ['directCostTotal', 'direct_cost_total', 'totalCost', 'total_cost']
        .filter(key => Object.prototype.hasOwnProperty.call(breakdown, key))
        .map(key => breakdown[key]);
    return values.length > 0 && values.every((value) => {
        if (value == null || String(value).trim() === '') return false;
        const parsed = Number(String(value).replace(/[$,\s]/g, ''));
        return Number.isFinite(parsed) && parsed === 0;
    });
}

function buildScopeLumpSumSourceRow(quoteRevenueExGst) {
    return {
        description: 'Scope lump sum',
        qty: 1,
        unit: 'Lump sum',
        unitRate: quoteRevenueExGst,
        total: quoteRevenueExGst,
        rateIsFinalChargeOut: true,
        rateTaxBasis: 'ex_gst',
        costRate: null,
        costRateTaxBasis: 'ex_gst',
        pricingStatus: 'manual_override',
        _rateSource: 'manual_override',
        manualOverride: true,
        internalPricingSource: SCOPE_LUMP_SUM_SOURCE,
    };
}

function canMaterializeScopeLumpSum(item, isGstInclusive) {
    if (!item || isSystemManaged(item)) return false;
    const breakdown = item.costBreakdown || item.cost_breakdown || {};
    const hasStoredRows = [...INTERNAL_PRICING_CATEGORIES, 'other'].some(category => (
        Array.isArray(breakdown[category]) && breakdown[category].length > 0
    ));
    if (hasStoredRows || number(breakdown.directCostTotal ?? breakdown.direct_cost_total) > 0) return false;
    const gstExempt = item.gstExempt === true || item.gst_exempt === true;
    return exGst(item.amount, isGstInclusive ? 'inc_gst' : 'ex_gst', gstExempt) > 0;
}

function materializeScopeLumpSum(item, isGstInclusive) {
    const breakdown = item.costBreakdown || item.cost_breakdown || {};
    const gstExempt = item.gstExempt === true || item.gst_exempt === true;
    const quoteRevenueExGst = exGst(item.amount, isGstInclusive ? 'inc_gst' : 'ex_gst', gstExempt);
    return {
        ...item,
        costBreakdown: {
            ...breakdown,
            otherCosts: [buildScopeLumpSumSourceRow(quoteRevenueExGst)],
        },
    };
}

function directCostResidual(breakdown) {
    const explicitDirectCost = number(breakdown?.directCostTotal ?? breakdown?.direct_cost_total);
    if (!(explicitDirectCost > 0)) return 0;
    const representedDirectCost = [
        ...INTERNAL_PRICING_CATEGORIES.filter(category => category !== 'otherCosts'),
        otherCostsSourceKey(breakdown),
    ]
        .reduce((sum, category) => {
            const rows = Array.isArray(breakdown?.[category]) ? breakdown[category] : [];
            return sum + rows.reduce((rowSum, row) => rowSum + number(row?.total), 0);
        }, 0);
    return round2(Math.max(0, explicitDirectCost - representedDirectCost));
}

function buildUnallocatedDirectCostLine(breakdown, scopeIndex) {
    const unallocated = directCostResidual(breakdown);
    if (unallocated < 0.01) return null;
    return addCommercialMetrics({
        id: `${scopeIndex}:otherCosts:unallocated-direct-cost`,
        scopeIndex,
        category: 'otherCosts',
        categoryLabel: CATEGORY_LABELS.otherCosts,
        rowIndex: -1,
        sourceRowIndices: [],
        label: 'Unallocated direct cost',
        unit: 'Lump sum',
        quantity: 1,
        quantityField: null,
        businessCostRateExGst: unallocated,
        sellRateExGst: null,
        businessCostTotalExGst: unallocated,
        sellTotalExGst: null,
        grossProfitExGst: null,
        grossMarginPct: null,
        sellAuthority: 'modelled',
        activePricingItemId: null,
        sourcePricingItemId: null,
        sourceName: null,
        contractorName: null,
        supplierId: null,
        costRateSource: 'legacy_unallocated_direct_cost',
        rateSource: 'legacy_unallocated_direct_cost',
        confirmableManualAuthority: false,
        pendingResolutionIdentity: null,
        costKnown: true,
        locked: true,
    });
}

function resolveQuantityField(row, category) {
    if (category === 'labour' || category === 'subcontractors') {
        const unit = lineUnit(row).toLowerCase();
        if (/day/.test(unit) && ('duration_days' in row || 'durationDays' in row)) {
            return 'duration_days' in row ? 'duration_days' : 'durationDays';
        }
        if (/hour|\bhr\b/.test(unit) || 'hours' in row) return 'hours';
    }
    if ('qty' in (row || {})) return 'qty';
    if ('quantity' in (row || {})) return 'quantity';
    return 'qty';
}

function addCommercialMetrics(row) {
    const cost = row.businessCostTotalExGst;
    const sell = row.sellTotalExGst;
    if (cost == null || sell == null) return row;
    const grossProfitExGst = round2(sell - cost);
    return {
        ...row,
        grossProfitExGst,
        grossMarginPct: sell > 0 ? round2((grossProfitExGst / sell) * 100) : null,
    };
}

function normalizedCommercialText(value) {
    return String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function catalogueRateExGst(row, fields, basisFields) {
    const raw = fields.map(field => row?.[field]).find(value => value != null && value !== '');
    if (raw == null) return null;
    const parsed = Number(String(raw).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(parsed)) return null;
    const basis = basisFields.map(field => row?.[field]).find(value => value != null && value !== '');
    return exGst(parsed, basis, false);
}

function approvedContractorSelection(selection, businessPricing, currentRow) {
    const pricingItemId = String(selection?.pricingItemId || '').trim();
    if (!pricingItemId || !businessPricing || typeof businessPricing !== 'object') return null;
    const candidate = (Array.isArray(businessPricing.subcontractors) ? businessPricing.subcontractors : [])
        .find(row => String(row?.id || '').trim() === pricingItemId);
    if (!candidate || !BusinessPricingService.isTrustedPricingItem(candidate)) return null;
    const candidateTrade = normalizedCommercialText(candidate.trade || candidate.name || candidate.item);
    const currentTrade = normalizedCommercialText(currentRow?.trade || currentRow?.description || currentRow?.name);
    const candidateUnit = normalisePricingUnitKey(candidate.unit);
    const currentUnit = normalisePricingUnitKey(currentRow?.unit);
    if (!candidateTrade || (currentTrade && candidateTrade !== currentTrade)) return null;
    if (!candidateUnit || (currentUnit && candidateUnit !== currentUnit)) return null;
    const sellRateExGst = catalogueRateExGst(
        candidate,
        ['rate', 'cost'],
        ['chargeOutTaxBasis', 'charge_out_tax_basis'],
    );
    if (!(sellRateExGst > 0)) return null;
    const rawBusinessCost = catalogueRateExGst(
        candidate,
        ['costRate', 'cost_rate'],
        ['costRateTaxBasis', 'cost_rate_tax_basis'],
    );
    return {
        pricingItemId,
        supplierId: String(candidate.supplierId || candidate.supplier_id || '').trim() || null,
        contractorName: String(candidate.contractorName || candidate.contractor_name || '').trim() || null,
        trade: String(candidate.trade || candidate.name || candidate.item || currentRow?.trade || '').trim(),
        unit: String(candidate.unit || currentRow?.unit || '').trim(),
        businessCostRateExGst: rawBusinessCost != null && rawBusinessCost > 0
            ? rawBusinessCost
            : null,
        sellRateExGst,
    };
}

function labourConsolidationKey(row) {
    return JSON.stringify([
        normalizedCommercialText(row.label),
        normalizedCommercialText(row.unit),
        nullableRound2(row.businessCostRateExGst),
        row.sellAuthority === 'modelled' ? null : nullableRound2(row.sellRateExGst),
        row.sellAuthority || null,
        normalizedCommercialText(row.activePricingItemId),
        normalizedCommercialText(row.sourcePricingItemId),
        normalizedCommercialText(row.costRateSource),
        normalizedCommercialText(row.rateSource),
        row.pendingResolutionIdentity,
        row.locked === true,
    ]);
}

const LABOUR_DRIVER_KEYS = Object.freeze([
    'qty', 'quantity', 'hours', 'duration_days', 'durationDays',
    'billableQuantity', 'billable_quantity', 'total',
]);

const hasStoredValue = (value) => value !== undefined && value !== null && value !== '';

/** A stored amount or quantity that reads as a finite number ("$1,234.50" does). */
function isParseableAmount(value) {
    if (typeof value === 'number') return Number.isFinite(value);
    return Number.isFinite(Number(String(value).replace(/[$,\s]/g, '')));
}

/**
 * The stored SHAPE of a saved labour line. Two rows are only equivalent to the
 * export, so that their quantities and amounts may be ADDED, when every stored
 * figure means the same thing:
 *   - the same tax basis: a total saved inc GST and one saved ex GST are never
 *     added, even when their ex-GST rates happen to agree;
 *   - the same crew: one worker for a day and two workers for a day are not
 *     two days of one crew (the billable quantity is days times workers);
 *   - the same driver fields: qty beside quantity, or days beside hours, are
 *     different drivers and would be double counted if summed field by field;
 *   - amounts that all parse. A figure that does not parse is unknown, never a
 *     silent zero, so that row stays its own line.
 */
function labourRawShapeSignature(sourceRow, rowIndex) {
    const raw = sourceRow && typeof sourceRow === 'object' ? sourceRow : {};
    const drivers = LABOUR_DRIVER_KEYS.filter(key => hasStoredValue(raw[key]));
    const workers = hasStoredValue(raw.workers) && isParseableAmount(raw.workers)
        ? Number(String(raw.workers).replace(/[$,\s]/g, ''))
        : 1;
    return JSON.stringify([
        normalizedCommercialText(raw.rateTaxBasis ?? raw.rate_tax_basis),
        raw.rateIsFinalChargeOut === true || raw.rate_is_final_charge_out === true,
        raw.rateIsFinalIncGst === true || raw.rate_is_final_inc_gst === true,
        workers > 0 ? workers : 1,
        drivers,
        drivers.some(key => !isParseableAmount(raw[key])) ? `unparseable:${rowIndex}` : null,
    ]);
}

function consolidateEquivalentLabourRows(rows, sourceRows = []) {
    const grouped = new Map();
    for (const row of rows) {
        const key = `${labourConsolidationKey(row)}|${labourRawShapeSignature(sourceRows[row.rowIndex], row.rowIndex)}`;
        const existing = grouped.get(key);
        if (!existing) {
            grouped.set(key, {
                ...row,
                sourceRowIndices: [row.rowIndex],
            });
            continue;
        }
        const businessCostTotalExGst = existing.businessCostTotalExGst == null
            || row.businessCostTotalExGst == null
            ? null
            : round2(existing.businessCostTotalExGst + row.businessCostTotalExGst);
        const sellTotalExGst = existing.sellTotalExGst == null
            || row.sellTotalExGst == null
            ? null
            : round2(existing.sellTotalExGst + row.sellTotalExGst);
        const quantity = round2(existing.quantity + row.quantity);
        grouped.set(key, addCommercialMetrics({
            ...existing,
            quantity,
            businessCostRateExGst: businessCostTotalExGst == null || quantity <= 0
                ? existing.businessCostRateExGst
                : round2(businessCostTotalExGst / quantity),
            sellRateExGst: sellTotalExGst == null || quantity <= 0
                ? existing.sellRateExGst
                : round2(sellTotalExGst / quantity),
            businessCostTotalExGst,
            sellTotalExGst,
            costKnown: existing.costKnown && row.costKnown,
            sourceRowIndices: [...existing.sourceRowIndices, row.rowIndex],
        }));
    }
    return [...grouped.values()];
}

function coverageFor(rows) {
    const chargeable = rows.filter(row => number(row.sellTotalExGst) > 0 || number(row.businessCostTotalExGst) > 0);
    const driverIncomplete = rows.some(row => row.driverNeeded === true);
    const costIncomplete = rows.some(row => !row.costKnown && row.driverNeeded !== true);
    if (chargeable.length === 0) return driverIncomplete || costIncomplete ? 'unknown' : 'none';
    const known = chargeable.filter(row => row.businessCostTotalExGst != null).length;
    if (known === chargeable.length && !driverIncomplete && !costIncomplete) return 'complete';
    return known > 0 ? 'partial' : 'unknown';
}

function summaryFor(rows, quoteRevenueExGst) {
    const coverage = coverageFor(rows);
    const missingCostCount = rows.filter(row => !row.costKnown && row.driverNeeded !== true).length;
    const missingDriverCount = rows.filter(row => row.driverNeeded === true).length;
    const knownCost = round2(rows.reduce((sum, row) => sum + number(row.businessCostTotalExGst), 0));
    const businessCostExGst = coverage === 'complete' || coverage === 'none' ? knownCost : null;
    const grossProfitExGst = businessCostExGst == null ? null : round2(quoteRevenueExGst - businessCostExGst);
    return {
        quoteRevenueExGst: round2(quoteRevenueExGst),
        businessCostExGst,
        grossProfitExGst,
        grossMarginPct: grossProfitExGst == null || quoteRevenueExGst <= 0
            ? null
            : round2((grossProfitExGst / quoteRevenueExGst) * 100),
        costCoverage: coverage,
        lineCount: rows.length,
        missingCostCount,
        missingDriverCount,
        incompletePricingCount: missingCostCount + missingDriverCount,
    };
}

function projectMetricsFor(summary, canonicalPricing) {
    const projectCostExGst = canonicalPricing?.actualBuilderCostExGst == null
        ? null
        : round2(canonicalPricing.actualBuilderCostExGst);
    const projectProfitExGst = canonicalPricing?.actualMarginAmount == null
        ? null
        : round2(canonicalPricing.actualMarginAmount);
    const projectMarginPct = canonicalPricing?.actualMarginPercent == null
        ? null
        : round2(canonicalPricing.actualMarginPercent);
    return {
        directBusinessCostExGst: summary.businessCostExGst,
        projectCostExGst,
        projectProfitExGst,
        projectMarginPct,
        projectCostCoverage: canonicalPricing?.actualCostCoverage || summary.costCoverage,
    };
}

const BRIDGE_TOLERANCE_EX_GST = 0.05;
// The producer rounds a line to `line_rounding_inc_gst`, default 10 (rj-bird-pricing-methodology.js). Whatever is left of
// the saved bridge after the stamped attendance top-up is rounding only while it stays within one such step.
const ROUNDING_STEP_SANITY_INC_GST = 10.05;

/**
 * Which part of the unallocated customer sell the item's SAVED pricing methodology explains.
 *
 * The canonical Stage 4 ledger already charges the customer banded rate-card sell + administration share + the
 * rounding bridge (buildLedgerItem). The rows of this ledger carry only the rate-card part, so a methodology item
 * read as an unexplained gap of exactly administration + rounding (RBB261040: 880.00 + 4.18 of 884.18).
 *
 * The stamps are trusted only when they validate against what is actually saved:
 *   none        no methodology id, or nothing stamped to explain
 *   invalid     a stamp is not a number, or an administration share / banded sell / attendance is negative, or the
 *               stamped attendance exceeds the whole bridge it is supposed to be part of
 *   not_needed  nothing is left unallocated (a priced modelled row already absorbed it)
 *   missing     the banded rate-card sell was never stamped, so the stamps cannot be tied to these rows
 *   stale       the rows no longer add up to the stamped base (a row sell changed after the stamp), or the stamps
 *               point the other way from the gap
 *   overlarge   the stamps claim more than the quote holds
 *   applied     explained = the stamped administration + the producer's rounding bridge, never more than what is left
 *               unallocated
 * Only `applied` explains any money. Everything else leaves the whole remainder visible, which is the safe direction.
 * The bridge is subtracted from what REMAINS after allocation, so it can never be counted on top of a row that
 * already absorbed it, and it never changes a row.
 *
 * The producer's `pricingRoundingDeltaIncGst` is the WHOLE step from the unrounded figure to the committed amount, so
 * it already contains the minimum-attendance top-up (`minimumAttendanceAllocationIncGst`). The explained total is
 * administration + that one number. The attendance is carved OUT of it for display, never added to it: a quote
 * topped up to its minimum would otherwise read as hundreds of dollars of "rounding". What cannot be itemised stays
 * honest: rounding is only what is left within one rounding step; anything larger that no attendance stamp accounts
 * for is reported as `otherAdjustmentExGst` and never called rounding.
 */
function methodologyBridgeFor({
    breakdown, itemLocked, gstExempt, fixedSell, remainderExGst, rateBookChargeOutExGst,
}) {
    const result = (status, fields = {}) => ({
        status,
        administrationExGst: 0,
        minimumAttendanceExGst: 0,
        roundingExGst: 0,
        otherAdjustmentExGst: 0,
        explainedExGst: 0,
        ...fields,
    });
    const stamps = readMethodologyBridgeStamps(breakdown);
    if (itemLocked || !stamps.methodologyId) return result('none');
    if (stamps.invalid) return result('invalid');
    // The attendance is part of the bridge, so it cannot exceed it by more than a rounding step (a rounding-down step
    // after the top-up leaves the bridge slightly smaller than the top-up).
    if (stamps.minimumAttendanceIncGst - stamps.roundingDeltaIncGst > ROUNDING_STEP_SANITY_INC_GST) {
        return result('invalid');
    }
    const gstFactor = gstExempt ? 1 : 1.1;
    const administrationExGst = round2(stamps.administrationShareExGst);
    const bridgeExGst = round2(stamps.roundingDeltaIncGst / gstFactor);
    const claimedExGst = round2(administrationExGst + bridgeExGst);
    const minimumAttendanceExGst = round2(stamps.minimumAttendanceIncGst / gstFactor);
    const afterAttendanceExGst = round2(bridgeExGst - minimumAttendanceExGst);
    const roundsWithinOneStep = Math.abs(stamps.roundingDeltaIncGst - stamps.minimumAttendanceIncGst)
        <= ROUNDING_STEP_SANITY_INC_GST;
    const roundingExGst = roundsWithinOneStep ? afterAttendanceExGst : 0;
    const otherAdjustmentExGst = roundsWithinOneStep ? 0 : afterAttendanceExGst;
    if (Math.abs(claimedExGst) < 0.005) return result('none');
    if (Math.abs(remainderExGst) < 0.005) return result('not_needed');
    if (!stamps.bandedSellPresent) return result('missing');
    // The canonical rate-book charge-out is banded sell + administration + rounding + the separate inc-GST stream.
    // Taking the components back out leaves the stamped base, which must be what these rows carry.
    const stampedBaseExGst = round2(number(rateBookChargeOutExGst) - claimedExGst);
    if (Math.abs(stampedBaseExGst - fixedSell) > BRIDGE_TOLERANCE_EX_GST) return result('stale');
    if (claimedExGst * remainderExGst <= 0) return result('stale');
    if (Math.abs(claimedExGst) > Math.abs(remainderExGst) + BRIDGE_TOLERANCE_EX_GST) return result('overlarge');
    const explainedExGst = round2(Math.sign(remainderExGst)
        * Math.min(Math.abs(claimedExGst), Math.abs(remainderExGst)));
    return result('applied', {
        administrationExGst, minimumAttendanceExGst, roundingExGst, otherAdjustmentExGst, explainedExGst,
    });
}

/** Build a read-only ex-GST estimator projection from the persisted scope. */
export function buildInternalPricingLedger(scope = [], { isGstInclusive = true } = {}) {
    const scopeItems = Array.isArray(scope) ? scope : [];
    const canonicalProjectPricing = buildStage4PricingLedger(scopeItems, isGstInclusive);
    const items = scopeItems.map((item, scopeIndex) => {
        const breakdown = item?.costBreakdown || item?.cost_breakdown || {};
        const gstExempt = item?.gstExempt === true || item?.gst_exempt === true;
        const itemLocked = isSystemManaged(item);
        const managedZeroCost = managedBreakdownDeclaresZeroCost(item, breakdown);
        const passThroughAtCost = isManagedZeroCostReceipt(item);
        const quoteRevenueExGst = exGst(item?.amount, isGstInclusive ? 'inc_gst' : 'ex_gst', gstExempt);
        const otherSourceKey = otherCostsSourceKey(breakdown);
        const categories = Object.fromEntries(INTERNAL_PRICING_CATEGORIES.map(category => {
            const sourceKey = category === 'otherCosts' ? otherSourceKey : category;
            const rows = Array.isArray(breakdown[sourceKey]) ? breakdown[sourceKey] : [];
            return [category, rows.map((row, rowIndex) => buildRawLine(
                row, sourceKey, rowIndex, scopeIndex, gstExempt, itemLocked, managedZeroCost, passThroughAtCost,
            ))];
        }));
        const unallocatedDirectCost = buildUnallocatedDirectCostLine(breakdown, scopeIndex);
        if (unallocatedDirectCost) categories.otherCosts.push(unallocatedDirectCost);
        let rows = INTERNAL_PRICING_CATEGORIES.flatMap(category => categories[category]);
        if (rows.length === 0 && quoteRevenueExGst > 0 && !itemLocked) {
            categories.otherCosts.push(buildRawLine(
                buildScopeLumpSumSourceRow(quoteRevenueExGst),
                'otherCosts',
                0,
                scopeIndex,
                gstExempt,
            ));
            rows = INTERNAL_PRICING_CATEGORIES.flatMap(category => categories[category]);
        }
        const fixedSell = round2(rows.reduce((sum, row) => sum + number(row.sellTotalExGst), 0));
        const modelled = rows.filter(row => (
            row.sellAuthority === 'modelled' && row.savedCommercialReview !== true
        ));
        const allocatableModelled = modelled.filter(row => row.driverNeeded !== true
            && (row.quantity > 0 || number(row.businessCostTotalExGst) > 0));
        const residualSell = round2(Math.max(0, quoteRevenueExGst - fixedSell));
        const modelledSellCents = allocateWeightedCents(residualSell, allocatableModelled);
        const modelledSellCentsById = new Map(
            allocatableModelled.map((row, index) => [row.id, modelledSellCents[index] || 0]),
        );
        rows = rows.map(row => {
            if (row.sellAuthority !== 'modelled' || row.savedCommercialReview === true) {
                return addCommercialMetrics(row);
            }
            if (!modelledSellCentsById.has(row.id)) return addCommercialMetrics({
                ...row,
                sellTotalExGst: null,
                sellRateExGst: null,
                sellDeferred: true,
            });
            const sellTotalExGst = round2(modelledSellCentsById.get(row.id) / 100);
            return addCommercialMetrics({
                ...row,
                sellTotalExGst,
                sellRateExGst: row.quantity > 0 ? round2(sellTotalExGst / row.quantity) : null,
            });
        });
        for (const category of INTERNAL_PRICING_CATEGORIES) {
            const categoryRows = rows.filter(row => row.category === category || (
                category === 'otherCosts' && row.category === 'other'
            ));
            categories[category] = category === 'labour'
                ? consolidateEquivalentLabourRows(
                    categoryRows,
                    Array.isArray(breakdown.labour) ? breakdown.labour : [],
                )
                : categoryRows;
        }
        rows = INTERNAL_PRICING_CATEGORIES.flatMap(category => categories[category]);
        const representedSell = round2(rows.reduce((sum, row) => sum + number(row.sellTotalExGst), 0));
        const missingManagedPricingReceiptCount = itemLocked && rows.length === 0 && quoteRevenueExGst > 0 ? 1 : 0;
        const missingCustomerPriceCount = quoteRevenueExGst <= 0 && representedSell <= 0 && !itemLocked ? 1 : 0;
        const canonicalItemPricing = buildStage4PricingLedger([item], isGstInclusive);
        const methodologyBridge = methodologyBridgeFor({
            breakdown,
            itemLocked,
            gstExempt,
            fixedSell,
            remainderExGst: round2(quoteRevenueExGst - representedSell),
            rateBookChargeOutExGst: canonicalItemPricing.items[0]?.rateBookChargeOutExGst,
        });
        const reconciliationExGst = missingCustomerPriceCount > 0 || missingManagedPricingReceiptCount > 0
            ? 0
            : round2(quoteRevenueExGst - representedSell - methodologyBridge.explainedExGst);
        const baseSummary = summaryFor(rows, quoteRevenueExGst);
        const directSummary = {
            ...baseSummary,
            ...(missingManagedPricingReceiptCount > 0 ? {
                businessCostExGst: null,
                grossProfitExGst: null,
                grossMarginPct: null,
                costCoverage: 'unknown',
            } : {}),
        };
        return {
            id: String(item?.id ?? item?.code ?? scopeIndex + 1),
            code: String(item?.code ?? scopeIndex + 1),
            title: String(item?.title || 'Untitled scope item'),
            scopeIndex,
            gstExempt,
            // A committed scope amount is not a read-only estimator record.
            // Internal Pricing is the explicit place where the estimator may
            // revise its cost and sell inputs. Only system-managed statutory
            // rows remain protected.
            locked: itemLocked,
            categories,
            rows,
            summary: {
                ...directSummary,
                ...projectMetricsFor(directSummary, canonicalItemPricing),
                missingManagedPricingReceiptCount,
                missingCustomerPriceCount,
                representedSellExGst: representedSell,
                methodologyBridge,
                reconciliationExGst,
            },
        };
    });
    const allRows = items.flatMap(item => item.rows);
    const totalRevenue = round2(items.reduce((sum, item) => sum + item.summary.quoteRevenueExGst, 0));
    const missingManagedPricingReceiptCount = items.reduce(
        (total, item) => total + item.summary.missingManagedPricingReceiptCount,
        0,
    );
    const projectSummary = summaryFor(allRows, totalRevenue);
    const directProjectSummary = {
        ...projectSummary,
        ...(missingManagedPricingReceiptCount > 0 ? {
            businessCostExGst: null,
            grossProfitExGst: null,
            grossMarginPct: null,
            costCoverage: allRows.length > 0 ? 'partial' : 'unknown',
        } : {}),
    };
    return {
        taxBasis: 'ex_gst',
        items,
        summary: {
            ...directProjectSummary,
            ...projectMetricsFor(directProjectSummary, canonicalProjectPricing),
            missingManagedPricingReceiptCount,
            missingCustomerPriceCount: items.reduce(
                (total, item) => total + item.summary.missingCustomerPriceCount,
                0,
            ),
        },
    };
}

function cloneScopeForEdit(scope) {
    return scope.map(item => {
        const breakdown = item?.costBreakdown || item?.cost_breakdown;
        if (!breakdown || typeof breakdown !== 'object') return item;
        const nextBreakdown = { ...breakdown };
        for (const category of [...INTERNAL_PRICING_CATEGORIES, 'other']) {
            if (Array.isArray(breakdown[category])) {
                nextBreakdown[category] = breakdown[category].map(row => ({ ...row }));
            }
        }
        return { ...item, costBreakdown: nextBreakdown };
    });
}

function clearMatchAuthority(row) {
    for (const key of MATCH_STAMPS) delete row[key];
    row.pricingItemId = null;
    delete row.pricing_item_id;
}

function setRowRate(row, category, value) {
    const field = priceWorkingRateField(row, category);
    row[field] = round2(value);
    return field;
}

function recomputeBreakdownReceipts(breakdown, preservedResidual = 0) {
    const partition = partitionCostLines(otherCostsSourceKey(breakdown) === 'other'
        ? { ...breakdown, otherCosts: undefined }
        : breakdown);
    breakdown.finalIncGstAddon = partition.finalIncGstTotal;
    breakdown.finalExGstAddon = partition.finalExGstTotal;
    breakdown.directCostTotal = round2(partition.lineSumTotal + Math.max(0, number(preservedResidual)));
}

/**
 * Apply explicit estimator edits to a project scope. This never receives or
 * mutates BusinessPricing. Global rate-card changes remain a Pricing-page job.
 */
export function applyInternalPricingLedgerEdits(scope, edits = [], opts = {}) {
    if (!Array.isArray(scope) || !Array.isArray(edits) || edits.length === 0) return scope;
    const pricingFingerprint = String(opts.pricingFingerprint || '').trim();
    const isGstInclusive = opts.isGstInclusive !== false;
    const itemsRequiringPricingResolution = new Set(scope.flatMap((item, index) => (
        scopeItemHasUnresolvedPricing(item) ? [index] : []
    )));
    const scopeLumpSumsToMaterialize = new Set();
    const directCostResidualByItem = new Map(scope.map((item, index) => [
        index,
        directCostResidual(item?.costBreakdown || item?.cost_breakdown || {}),
    ]));
    for (const edit of edits) {
        const item = scope[edit?.scopeIndex];
        if (isSystemManaged(item)) return scope;
        const breakdown = item?.costBreakdown || item?.cost_breakdown;
        const sourceKey = edit?.category === 'otherCosts'
            ? otherCostsSourceKey(breakdown)
            : edit?.category;
        const rowIndices = Array.isArray(edit?.rowIndices) && edit.rowIndices.length > 0
            ? [...new Set(edit.rowIndices)]
            : [edit?.rowIndex];
        const materializesScopeLumpSum = edit?.syntheticSource === SCOPE_LUMP_SUM_SOURCE
            && edit?.category === 'otherCosts'
            && rowIndices.length === 1
            && rowIndices[0] === 0
            && canMaterializeScopeLumpSum(item, isGstInclusive);
        if (materializesScopeLumpSum) {
            scopeLumpSumsToMaterialize.add(edit.scopeIndex);
            continue;
        }
        if (!INTERNAL_PRICING_CATEGORIES.includes(edit?.category)
            || !Array.isArray(breakdown?.[sourceKey])
            || rowIndices.some(rowIndex => !Number.isInteger(rowIndex) || !breakdown[sourceKey][rowIndex])) return scope;
        if (rowIndices.some(rowIndex => isSystemManaged(breakdown[sourceKey][rowIndex]))) return scope;
        if (edit?.pricingSelection && sourceKey === 'subcontractors'
            && !approvedContractorSelection(
                edit.pricingSelection,
                opts.businessPricing,
                breakdown[sourceKey][rowIndices[0]],
            )) return scope;
    }

    const baselineRowsBySource = new Map();
    for (const item of buildInternalPricingLedger(scope, {
        isGstInclusive: opts.isGstInclusive !== false,
    }).items) {
        for (const row of item.rows) {
            for (const rowIndex of row.sourceRowIndices || [row.rowIndex]) {
                const category = row.category === 'other' ? 'otherCosts' : row.category;
                baselineRowsBySource.set(`${row.scopeIndex}:${category}:${rowIndex}`, row);
            }
        }
    }

    const next = cloneScopeForEdit(scope);
    for (const scopeIndex of scopeLumpSumsToMaterialize) {
        next[scopeIndex] = materializeScopeLumpSum(next[scopeIndex], isGstInclusive);
    }
    const changedItems = new Set();
    const resolvedIdentitiesByItem = new Map();
    let requiresReprice = false;
    for (const edit of edits) {
        const item = next[edit.scopeIndex];
        const breakdown = item.costBreakdown;
        const sourceKey = edit.category === 'otherCosts'
            ? otherCostsSourceKey(breakdown)
            : edit.category;
        const rowIndices = Array.isArray(edit.rowIndices) && edit.rowIndices.length > 0
            ? [...new Set(edit.rowIndices)]
            : [edit.rowIndex];
        for (const rowIndex of rowIndices) {
            const row = breakdown[sourceKey][rowIndex];
            const quantity = lineQuantity(row, sourceKey);
            const originalResolutionIdentity = pricingResolutionIdentity(row, sourceKey, rowIndex);
            const pendingBefore = pricingRowHasPendingResolution(row);
            const pendingStatus = row.pricingStatus ?? row.pricing_status;
            const pendingSource = row._rateSource ?? row._rate_source;
            let authorityEstablished = false;

            if (edit.pricingSelection && sourceKey === 'subcontractors') {
                const selection = approvedContractorSelection(
                    edit.pricingSelection,
                    opts.businessPricing,
                    row,
                );
                if (!selection) return scope;
                const previousPricingItemId = row.pricingItemId || row.pricing_item_id || null;
                const previousMatchedName = row.matchedName || row.matched_name || null;
                if (!row.sourcePricingItemId && !row.source_pricing_item_id && previousPricingItemId) {
                    row.sourcePricingItemId = previousPricingItemId;
                }
                if (!row.sourceMatchedName && !row.source_matched_name && previousMatchedName) {
                    row.sourceMatchedName = previousMatchedName;
                }
                row.trade = String(selection.trade || row.trade || '').trim();
                row.contractorName = String(selection.contractorName || '').trim() || null;
                row.supplierId = String(selection.supplierId || '').trim() || null;
                row.pricingItemId = String(selection.pricingItemId || '').trim() || null;
                delete row.pricing_item_id;
                row.unit = String(selection.unit || row.unit || '').trim();
                row.rate = round2(selection.sellRateExGst);
                row.costRate = nullableRound2(selection.businessCostRateExGst);
                delete row.cost_rate;
                row.total = round2(quantity * row.rate);
                row.rateTaxBasis = 'ex_gst';
                row.costRateTaxBasis = 'ex_gst';
                delete row.cost_rate_tax_basis;
                row.costRateSource = 'business';
                delete row.cost_rate_source;
                row.rateIsFinalChargeOut = true;
                row.rateIsFinalIncGst = false;
                row._rateSource = 'business';
                row.pricingStatus = 'matched';
                row.confidence = 'exact';
                row.matchedBy = 'builder_supplier_selection';
                row.matchedName = row.trade;
                row.matchedContractorName = row.contractorName;
                row.matchedSupplierId = row.supplierId;
                row.matchedUnit = row.unit;
                row.matchedRate = row.rate;
                delete row.manualOverride;
                row.supplierSelectedAt = new Date().toISOString();
                row.supplierSelectionConfirmed = true;
                authorityEstablished = !!row.pricingItemId && quantity > 0 && row.rate > 0;
                requiresReprice = true;
            }

            const finalBefore = isFinalChargeOut(row);

            if (edit.clearBusinessCost === true) {
                if (!finalBefore) {
                    const baseline = baselineRowsBySource.get(`${edit.scopeIndex}:${edit.category}:${rowIndex}`);
                    const sellRate = nullableRound2(baseline?.sellRateExGst);
                    if (sellRate == null) return scope;
                    const sourcePricingItemId = row.pricingItemId || row.pricing_item_id || row.sourcePricingItemId;
                    const sourceMatchedName = row.matchedName || row.matched_name || row.sourceMatchedName;
                    setRowRate(row, sourceKey, sellRate);
                    row.total = round2(quantity * sellRate);
                    row.rateTaxBasis = 'ex_gst';
                    row.rateIsFinalChargeOut = true;
                    row.rateIsFinalIncGst = false;
                    row._rateSource = 'manual_override';
                    row.pricingStatus = 'manual_override';
                    row.manualOverride = true;
                    if (sourcePricingItemId) row.sourcePricingItemId = sourcePricingItemId;
                    if (sourceMatchedName) row.sourceMatchedName = sourceMatchedName;
                    clearMatchAuthority(row);
                    authorityEstablished = !pendingBefore && quantity > 0 && sellRate > 0;
                }
                row.costRate = null;
                delete row.cost_rate;
                row.costRateTaxBasis = 'ex_gst';
                delete row.cost_rate_tax_basis;
                row.costRateSource = 'project_cleared';
                delete row.cost_rate_source;
                row.costRateEditedAt = new Date().toISOString();
            } else if (edit.businessCostRateExGst != null) {
                const costRate = Math.max(0, number(edit.businessCostRateExGst));
                if (finalBefore || edit.sellRateExGst != null) {
                    row.costRate = round2(costRate);
                    delete row.cost_rate;
                    row.costRateTaxBasis = 'ex_gst';
                    delete row.cost_rate_tax_basis;
                    row.costRateSource = 'project_manual';
                    delete row.cost_rate_source;
                    row.costRateEditedAt = new Date().toISOString();
                } else {
                    setRowRate(row, sourceKey, costRate);
                    row.total = round2(quantity * costRate);
                    row.rateTaxBasis = 'ex_gst';
                    row._rateSource = 'project_cost_estimate';
                    row.pricingStatus = 'manual_cost_estimate';
                    authorityEstablished = quantity > 0 && costRate > 0;
                    requiresReprice = true;
                }
            }

            if (edit.sellRateExGst != null) {
                const sellRate = Math.max(0, number(edit.sellRateExGst));
                if (!finalBefore && !pendingBefore
                    && row.costRate == null && edit.clearBusinessCost !== true) {
                    const previousCostRate = quantity > 0
                        ? number(row.total) / quantity
                        : number(row[priceWorkingRateField(row, sourceKey)]);
                    row.costRate = round2(previousCostRate);
                    delete row.cost_rate;
                    row.costRateTaxBasis = 'ex_gst';
                    delete row.cost_rate_tax_basis;
                    row.costRateSource = 'project_estimate';
                    delete row.cost_rate_source;
                }
                const sourcePricingItemId = row.pricingItemId || row.pricing_item_id || row.sourcePricingItemId;
                const sourceMatchedName = row.matchedName || row.matched_name || row.sourceMatchedName;
                setRowRate(row, sourceKey, sellRate);
                row.total = round2(quantity * sellRate);
                row.rateTaxBasis = 'ex_gst';
                row.rateIsFinalChargeOut = true;
                row.rateIsFinalIncGst = false;
                row._rateSource = 'manual_override';
                row.pricingStatus = 'manual_override';
                row.manualOverride = true;
                row.sellRateEditedAt = new Date().toISOString();
                if (sourcePricingItemId) row.sourcePricingItemId = sourcePricingItemId;
                if (sourceMatchedName) row.sourceMatchedName = sourceMatchedName;
                clearMatchAuthority(row);
                authorityEstablished = quantity > 0 && sellRate > 0;
                requiresReprice = true;
            }

            const currentSource = normalizedCommercialText(row._rateSource ?? row._rate_source);
            const currentStatus = normalizedCommercialText(row.pricingStatus ?? row.pricing_status);
            const currentAuthorityIsManual = row.manualOverride === true
                || ['manual_override', 'manual_cost_estimate'].includes(currentStatus)
                || ['manual', 'manual_override', 'project_manual', 'project_cost_estimate'].includes(currentSource);
            if (edit.confirmManualAuthority === true && pendingBefore
                && currentAuthorityIsManual && quantity > 0 && number(row.total) > 0) {
                authorityEstablished = true;
                requiresReprice = true;
            }
            const overlapPurpose = String(edit.confirmIncludedLabourOverlapPurpose || '').trim();
            const pendingReason = normalizedCommercialText(
                row.unresolvedReason ?? row.unresolved_reason,
            );
            if (sourceKey === 'labour' && pendingBefore
                && pendingReason === 'included_labour_overlap'
                && overlapPurpose.length > 0
                && number(row.total) > 0) {
                row.includedLabourOverlapConfirmed = true;
                row.includedLabourOverlapPurpose = overlapPurpose;
                row.includedLabourOverlapConfirmedAt = new Date().toISOString();
                delete row.included_labour_overlap_confirmed;
                delete row.included_labour_overlap_purpose;
                delete row.included_labour_overlap_confirmed_at;
                authorityEstablished = true;
                requiresReprice = true;
            }
            if (authorityEstablished && itemsRequiringPricingResolution.has(edit.scopeIndex)
                && !pricingFingerprint) {
                authorityEstablished = false;
            }

            if (authorityEstablished && originalResolutionIdentity) {
                clearCurrentPricingResolutionState(row);
                if (!row.pricingStatus) {
                    row.pricingStatus = isFinalChargeOut(row)
                        ? (row.pricingItemId ? 'matched' : 'manual_override')
                        : 'manual_cost_estimate';
                }
                if (!row._rateSource) {
                    row._rateSource = isFinalChargeOut(row)
                        ? (row.pricingItemId ? 'business' : 'manual_override')
                        : 'project_cost_estimate';
                }
                const identities = resolvedIdentitiesByItem.get(edit.scopeIndex) || [];
                identities.push(originalResolutionIdentity);
                resolvedIdentitiesByItem.set(edit.scopeIndex, identities);
            } else if (pendingBefore) {
                if (pendingStatus != null) row.pricingStatus = pendingStatus;
                if (pendingSource != null) row._rateSource = pendingSource;
            }
        }
        recomputeBreakdownReceipts(breakdown, directCostResidualByItem.get(edit.scopeIndex));
        changedItems.add(edit.scopeIndex);
    }

    for (const [scopeIndex, identities] of resolvedIdentitiesByItem) {
        next[scopeIndex] = reconcileScopeItemPricingResolution(next[scopeIndex], identities, {
            costSource: 'internal_pricing_edit',
            pricingFingerprint,
        });
    }

    // Cost-to-business edits on final sell lines do not alter the quote. Avoid
    // a pointless repricing pass and preserve the exact committed amount.
    if (!requiresReprice) {
        return next.map((item, index) => {
            if (!changedItems.has(index)) return item;
            const unresolved = scopeItemHasUnresolvedPricing(item, {
                ignoreAmountAuthority: resolvedIdentitiesByItem.has(index),
            });
            return {
                ...item,
                editedBy: 'user',
                costBreakdown: unresolved
                    ? item.costBreakdown
                    : { ...item.costBreakdown, costSource: 'internal_pricing_edit' },
            };
        });
    }

    let computed;
    try {
        const defaultMarginPct = opts.marginPercent == null || opts.marginPercent === ''
            ? undefined
            : nullableRound2(opts.marginPercent) ?? undefined;
        computed = calculateProjectTotals(
            mapScopeToInputs(next, {
                isGstInclusive: opts.isGstInclusive !== false,
                defaultMarginPct,
                rateFinalNoMarkup: true,
            }),
            buildCostConfig(opts.costingConfig || {}),
        )?.items;
    } catch {
        return scope;
    }
    if (!Array.isArray(computed) || computed.length !== next.length) return scope;

    return next.map((item, index) => {
        if (!changedItems.has(index)) return item;
        if (scopeItemHasUnresolvedPricing(item, {
            ignoreAmountAuthority: resolvedIdentitiesByItem.has(index),
        })) {
            return { ...item, editedBy: 'user' };
        }
        const line = computed[index];
        if (!line) return scope[index];
        const isGstInclusive = opts.isGstInclusive !== false;
        const amount = isGstInclusive ? number(line.incGst) : number(line.exGst);
        return {
            ...item,
            amount: round2(amount).toFixed(2),
            amountAuthority: 'committed',
            editedBy: 'user',
            costBreakdown: {
                ...item.costBreakdown,
                costSource: 'internal_pricing_edit',
                totalCost: round2(line.totalCost),
                contingencyAmount: round2(line.contingencyAmount),
                overheadAmount: round2(line.overheadAmount),
                marginPct: round2(line.marginPct),
                marginAmount: round2(line.marginAmount),
            },
        };
    });
}
